import { Trans } from "@lingui/macro";
import { UiButton, UiDialogResponsive } from "@smp/ui";
import { createContext, PropsWithChildren, useContext, useState } from "react";

import { DIALOG_CONFIG_INITIAL } from "./confirm.static";
import {
  IContextConfirm,
  IContextConfirmConfig,
  IContextConfirmDialogOptions,
} from "./confirm.types";

const ContextConfirm = createContext<IContextConfirm>(
  null as unknown as IContextConfirm,
);

export const ProviderConfirm = ({ children }: PropsWithChildren<{}>) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [config, setConfig] = useState<IContextConfirmConfig>(
    DIALOG_CONFIG_INITIAL,
  );
  const [dialogOptions, setDialogOptions] = useState<
    IContextConfirmDialogOptions | undefined
  >(undefined);

  const dialogOpen: IContextConfirm["dialogOpen"] = (options, config?) => {
    config && setConfig(config);
    setDialogOptions(options);
    setIsDialogOpen(true);
  };

  const dialogReset = () => {
    setConfig(DIALOG_CONFIG_INITIAL);
    setDialogOptions(undefined);
  };

  const onConfirm = () => {
    dialogOptions && dialogOptions.cb(true);
    setIsDialogOpen(false);
  };

  const onDismiss = () => {
    dialogOptions && dialogOptions.cb(false);
    setIsDialogOpen(false);
  };

  return (
    <ContextConfirm.Provider value={{ dialogOpen, onConfirm, onDismiss }}>
      {children}
      <UiDialogResponsive
        actionsRight={[
          <UiButton onClick={onConfirm} {...config.ButtonConfirmProps}>
            {config.ButtonConfirmText || <Trans>OK</Trans>}
          </UiButton>,
        ]}
        cancelButtonProps={config.ButtonCancelProps}
        cancelButtonText={config.ButtonCancelText || <Trans>Cancel</Trans>}
        fullWidth
        maxWidth="sm"
        onClose={onDismiss}
        open={isDialogOpen}
        title={dialogOptions?.title}
        onTransitionExited={dialogReset}
      >
        {dialogOptions?.message}
      </UiDialogResponsive>
    </ContextConfirm.Provider>
  );
};

export const useConfirm = (config?: IContextConfirmConfig) => {
  const { dialogOpen } = useContext(ContextConfirm);

  const getConfirmation = (
    options: Omit<IContextConfirmDialogOptions, "cb">,
  ): Promise<boolean> =>
    new Promise((res) => {
      dialogOpen({ ...options, cb: res }, config);
    });

  return { getConfirmation };
};
